import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Each link can navigate to a route (route:) and/or scroll to an anchor (anchor:)
const LINKS = [
  { label: "Capital Funding", route: "/capital-funding" },
  { label: "FMCG Brands", route: "/fmcg" },
  { label: "Leadership", route: "/", anchor: "#leadership" },
  { label: "About", route: "/", anchor: "#about" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (link) => {
    setOpen(false);
    const { route, anchor } = link;
    if (route && location.pathname !== route) {
      navigate(route);
      if (anchor) {
        // wait for the page to render then scroll
        setTimeout(() => {
          const el = document.querySelector(anchor);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 220);
      }
    } else if (anchor) {
      const el = document.querySelector(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleLogo = () => {
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goToContact = () => {
    setOpen(false);
    // contact is rendered on every route
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const isActive = (link) => {
    if (link.route && location.pathname === link.route && !link.anchor) {
      return true;
    }
    return false;
  };

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#051024]/95 backdrop-blur-xl border-b border-white/10"
          : "bg-gradient-to-b from-[#051024]/80 to-[#051024]/0 backdrop-blur-sm"
      }`}
    >
      <div className="cgp-container flex items-center justify-between h-20">
        <button
          data-testid="nav-logo-link"
          onClick={handleLogo}
          className="flex items-center gap-3"
        >
          <img
            src="/assets/catalyst-logo.png"
            alt="Catalyst Global Partners"
            className="h-10 w-auto bg-white/95 rounded px-2 py-1"
          />
          <span className="hidden sm:flex flex-col leading-tight text-left">
            <span className="font-serif text-white text-lg tracking-tight">
              Catalyst Global Partners
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#C8A24A]">
              Fueling Growth · Creating Value
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`cgp-nav-link text-sm font-medium tracking-wide ${
                isActive(l) ? "active" : ""
              }`}
              onClick={() => handleNav(l)}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <button
          data-testid="nav-cta-get-in-touch"
          onClick={goToContact}
          className="hidden md:inline-flex items-center gap-2 cgp-btn-gold rounded-full px-5 py-2.5 text-sm"
        >
          Get in Touch
          <ArrowUpRight size={16} strokeWidth={2.2} />
        </button>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-[#051024] border-t border-white/10"
          >
            <div className="cgp-container py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => handleNav(l)}
                  className="text-left text-white/85 hover:text-[#C8A24A] py-2 text-base"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={goToContact}
                className="cgp-btn-gold rounded-full px-5 py-3 text-sm self-start"
              >
                Get in Touch
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
